// src/scene/useResponsiveLayout.ts
/**
 * Responsive Layout System
 *
 * Viewport-aware layout helper for 3D UI positioning.
 * Returns panel anchors, scale factors, and breakpoint info.
 * No hardcoded pixel values - everything derives from tokens.
 */

import { useState, useEffect, useMemo } from 'react'
import {
  breakpoints,
  responsiveScale,
  panelSize,
  typography,
  spacing,
  Breakpoint,
} from './tokens'

// ============================================================================
// RESPONSIVE VALUES INTERFACE
// ============================================================================

// Desktop layout requires both minimum width AND landscape aspect ratio.
// Prevents portrait tablets (e.g. 980×1400) from triggering 3-panel layout.
export const DESKTOP_LAYOUT_MIN_WIDTH = 960
export const DESKTOP_LAYOUT_MIN_ASPECT = 1.2
export const DESKTOP_LAYOUT_THRESHOLD = 1024 // Full desktop vs compact desktop

interface ResponsiveValues {
  // Breakpoint info
  breakpoint: Breakpoint
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean

  // Desktop multi-panel mode (all panels visible at once)
  isDesktopLayout: boolean

  // Viewport dimensions
  width: number
  height: number
  aspect: number

  // Scale multipliers
  scale: number
  cameraMultiplier: number

  // Panel dimensions
  panelWidth: number

  // Typography (scaled)
  fontSize: {
    xs: number
    sm: number
    md: number
    lg: number
    xl: number
    '2xl': number
  }

  // Spacing (scaled)
  spacing: {
    panel: number
    card: number
    section: number
    item: number
    inline: number
  }
}

// ============================================================================
// BREAKPOINT DETECTION
// ============================================================================

function getBreakpoint(width: number, aspect: number): Breakpoint {
  if (width <= breakpoints.mobile.maxWidth || aspect <= breakpoints.mobile.maxAspect) {
    return 'mobile'
  }
  if (width <= breakpoints.tablet.maxWidth || aspect <= breakpoints.tablet.maxAspect) {
    return 'tablet'
  }
  return 'desktop'
}

// ============================================================================
// MAIN HOOK
// ============================================================================

export function useResponsiveLayout(): ResponsiveValues {
  // Track window size
  const [windowSize, setWindowSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1920,
    height: typeof window !== 'undefined' ? window.innerHeight : 1080,
  }))

  // Listen to resize events
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    // Debounce resize for performance
    let timeout: ReturnType<typeof setTimeout>
    const debouncedResize = () => {
      clearTimeout(timeout)
      timeout = setTimeout(handleResize, 100)
    }

    window.addEventListener('resize', debouncedResize)
    return () => {
      window.removeEventListener('resize', debouncedResize)
      clearTimeout(timeout)
    }
  }, [])

  // Compute all responsive values
  const values = useMemo((): ResponsiveValues => {
    const { width, height } = windowSize
    const aspect = width / height
    const breakpoint = getBreakpoint(width, aspect)

    const isMobile = breakpoint === 'mobile'
    const isTablet = breakpoint === 'tablet'
    const isDesktop = breakpoint === 'desktop'

    // Desktop layout: show all panels at once when wide enough AND landscape
    const isDesktopLayout = width >= DESKTOP_LAYOUT_MIN_WIDTH && aspect >= DESKTOP_LAYOUT_MIN_ASPECT

    // Get scale factors for current breakpoint
    const scaleFactors = responsiveScale[breakpoint]

    // Calculate scaled values
    const scale = scaleFactors.ui
    const cameraMultiplier = scaleFactors.camera
    const fontScale = scaleFactors.fontSize
    const panelScale = scaleFactors.panelWidth

    // Panel width
    const panelWidth = panelSize.width.md * panelScale

    // Scaled font sizes
    const fontSize = {
      xs: Math.round(typography.size.xs * fontScale),
      sm: Math.round(typography.size.sm * fontScale),
      md: Math.round(typography.size.md * fontScale),
      lg: Math.round(typography.size.lg * fontScale),
      xl: Math.round(typography.size.xl * fontScale),
      '2xl': Math.round(typography.size['2xl'] * fontScale),
    }

    // Scaled spacing
    const spacingScale = isMobile ? 0.8 : isTablet ? 0.9 : 1
    const scaledSpacing = {
      panel: Math.round(spacing.panelPadding * spacingScale),
      card: Math.round(spacing.cardPadding * spacingScale),
      section: Math.round(spacing.sectionGap * spacingScale),
      item: Math.round(spacing.itemGap * spacingScale),
      inline: Math.round(spacing.inlineGap * spacingScale),
    }

    return {
      breakpoint,
      isMobile,
      isTablet,
      isDesktop,
      isDesktopLayout,
      width,
      height,
      aspect,
      scale,
      cameraMultiplier,
      panelWidth,
      fontSize,
      spacing: scaledSpacing,
    }
  }, [windowSize])

  return values
}

// ============================================================================
// SPECIFIC HOOKS
// ============================================================================

/**
 * Hook for camera-specific responsive adjustments
 */
export function useResponsiveCamera() {
  const { cameraMultiplier, breakpoint } = useResponsiveLayout()

  return useMemo(() => ({
    multiplier: cameraMultiplier,
    breakpoint,
    // Y offset for better framing
    yOffset: breakpoint === 'mobile' ? 0.2 : breakpoint === 'tablet' ? 0.1 : 0,
  }), [cameraMultiplier, breakpoint])
}

/**
 * Hook for panel positioning
 */
export function usePanelLayout() {
  const { panelWidth, spacing, breakpoint, scale: uiScale } = useResponsiveLayout()

  return useMemo(() => ({
    width: panelWidth,
    padding: spacing.panel,
    gap: spacing.section,
    // Offset panels on mobile to fit screen better
    xOffset: breakpoint === 'mobile' ? 0.3 : breakpoint === 'tablet' ? 0.15 : 0,
    scale: breakpoint === 'mobile' ? 0.9 : breakpoint === 'tablet' ? 0.95 : uiScale,
  }), [panelWidth, spacing, breakpoint, uiScale])
}

/**
 * Hook for typography
 */
export function useTypography() {
  const { fontSize } = useResponsiveLayout()
  return fontSize
}

/**
 * Hook for spacing
 */
export function useSpacing() {
  const { spacing: spacingValues } = useResponsiveLayout()
  return spacingValues
}

// ============================================================================
// UTILITY: CSS MEDIA QUERY EQUIVALENT
// ============================================================================

/**
 * Returns true if viewport matches the given breakpoint or smaller
 */
export function useMediaQuery(query: 'mobile' | 'tablet' | 'desktop'): boolean {
  const { breakpoint } = useResponsiveLayout()

  if (query === 'mobile') return breakpoint === 'mobile'
  if (query === 'tablet') return breakpoint === 'mobile' || breakpoint === 'tablet'
  return true
}

// ============================================================================
// FRUSTUM CALCULATION
// ============================================================================

/**
 * Compute visible area at given Z-depth from camera.
 * Returns width and height of the visible frustum slice.
 */
function computeFrustumBounds(
  cameraZ: number,
  targetZ: number,
  fov: number,
  aspect: number
): { width: number; height: number } {
  const distance = cameraZ - targetZ
  const height = 2 * distance * Math.tan((fov * Math.PI / 180) / 2)
  const width = height * aspect
  return { width, height }
}

// ============================================================================
// DESKTOP LAYOUT POSITIONS (STATIC DEFAULTS)
// ============================================================================

/**
 * Static default positions for all panels in desktop layout mode.
 * These are fallbacks - use useDesktopPanelPositions() for frustum-aware positions.
 */
export const DESKTOP_PANEL_POSITIONS = {
  intro: {
    position: [-0.75, 0.61, 1.75] as [number, number, number],
    rotation: [-0.15, 0, 0] as [number, number, number],
  },
  experience: {
    position: [0, -0.05, 1.75] as [number, number, number],
    rotation: [-0.15, 0, 0] as [number, number, number],
  },
  projects: {
    position: [0.7, 0.56, 1.75] as [number, number, number],
    rotation: [-0.15, 0, 0] as [number, number, number],
  },
  skills: {
    position: [0, 0.15, 2] as [number, number, number],
    rotation: [-0.15, 0, 0] as [number, number, number],
  },
}

/**
 * Desktop camera settings
 */
export const DESKTOP_CAMERA = {
  position: [0, 0.7, 5.37] as [number, number, number],
  target: [0, 0.425, 2.157] as [number, number, number],
  fov: 24.5,
  near: 0.1,
  far: 1000,
}

/**
 * Mobile camera settings
 */
export const MOBILE_CAMERA = {
  position: [0, 0.7, 3.5] as [number, number, number],
  fov: 50,
}

// ============================================================================
// TABLET/DESKTOP LAYOUT HOOK
// ============================================================================

/**
 * Hook for desktop/tablet layout mode detection
 */
export function useDesktopLayout() {
  const { isDesktopLayout, width } = useResponsiveLayout()

  return useMemo(() => ({
    // Tablet and desktop both show all panels (tablet is more compact)
    isDesktopLayout,
    isTablet: isDesktopLayout && width < DESKTOP_LAYOUT_THRESHOLD,
    isFullDesktop: isDesktopLayout && width >= DESKTOP_LAYOUT_THRESHOLD,
    panelPositions: DESKTOP_PANEL_POSITIONS,
    camera: DESKTOP_CAMERA,
    // Transition smoothness
    transitionDuration: 0.5,
  }), [isDesktopLayout, width])
}

// ============================================================================
// FRUSTUM-RELATIVE POSITIONING HOOKS
// ============================================================================

/**
 * Hook that computes safe frustum bounds for panel placement.
 * Returns dimensions that guarantee visibility at the panel Z-depth.
 */
export function useFrustumLayout() {
  const { aspect } = useResponsiveLayout()
  const { isDesktopLayout, isTablet } = useDesktopLayout()

  return useMemo(() => {
    const cameraZ = isDesktopLayout ? DESKTOP_CAMERA.position[2] : MOBILE_CAMERA.position[2]
    const fov = isDesktopLayout ? DESKTOP_CAMERA.fov : MOBILE_CAMERA.fov
    const panelZ = 1.75

    const frustum = computeFrustumBounds(cameraZ, panelZ, fov, aspect)

    // Use 80% margins to avoid edge clipping, tighter on tablet
    const marginFactor = isTablet ? 0.85 : 0.8

    return {
      safeWidth: frustum.width * marginFactor,
      safeHeight: frustum.height * marginFactor,
      panelZ,
      frustumWidth: frustum.width,
      frustumHeight: frustum.height,
      aspect, // Pass through for spread calculations
    }
  }, [aspect, isDesktopLayout, isTablet])
}

/**
 * Hook that returns frustum-relative positions for desktop panels.
 * Positions are computed as percentages of visible frustum at panel Z-depth.
 * This guarantees panels are always visible regardless of viewport aspect ratio.
 */
export function useDesktopPanelPositions() {
  const { safeWidth, safeHeight, panelZ, aspect } = useFrustumLayout()
  const { isTablet } = useDesktopLayout()

  return useMemo(() => {
    // Base panel width in world units (400px * 0.002)
    const basePanelWidth = 0.8

    // Target gap grows proportionally with viewport width
    // Ensures visible separation at medium widths (~1400px) instead of only at ~1800px+
    const targetGap = Math.max(0, (safeWidth - 1.5) * 0.08)

    // Fit panels + gaps into safe width: 3*panelWidth + 2*gap = safeWidth
    const targetPanelWidth = (safeWidth - 2 * targetGap) / 3
    const scaleFactor = Math.min(1.0, Math.max(0.8, targetPanelWidth / basePanelWidth))

    // Apply scale to panel width
    const panelWidth = basePanelWidth * scaleFactor

    // Gap is the remaining space after fitting panels
    const gap = (safeWidth - 3 * panelWidth) / 2

    // Calculate X spread: center-to-center distance
    const spreadX = panelWidth + gap

    // Y positioning - intro/projects higher, experience lower
    const spreadY =  0.35
    const lowerY = 0.1

    return {
      intro: {
        position: [-spreadX, safeHeight * spreadY, panelZ] as [number, number, number],
        rotation: [-0.15, 0, 0] as [number, number, number],
      },
      experience: {
        position: [0, -safeHeight * lowerY, panelZ] as [number, number, number],
        rotation: [-0.15, 0, 0] as [number, number, number],
      },
      projects: {
        position: [spreadX, safeHeight * spreadY, panelZ] as [number, number, number],
        rotation: [-0.15, 0, 0] as [number, number, number],
      },
      skills: {
        position: [0, safeHeight * 0.1, panelZ + 0.25] as [number, number, number],
        rotation: [-0.15, 0, 0] as [number, number, number],
      },
      // Expose scale factor for panel sizing
      scaleFactor,
      // Flag when panels are too small for good UX
      shouldUseMobileLayout: scaleFactor < 0.6,
    }
  }, [safeWidth, safeHeight, panelZ, isTablet, aspect])
}

export default useResponsiveLayout
